import React, { useState, useEffect, useRef } from 'react';
import Header from '../../components/Header';
import { styles, colors } from '../../styles/theme';
import { searchMatch } from '../../utils/searchMatch';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const tipRengi = (tip) => ({
  TAM: { bg: '#e8f5e9', icon: '✅', color: '#2e7d32', label: 'Tam Eşleşme' },
  ALIAS: { bg: '#e3f2fd', icon: '⚡', color: '#1565c0', label: 'Hatırlanan' },
  YUKSEK: { bg: '#fff3e0', icon: '⚠️', color: '#f57c00', label: 'Yüksek Güven' },
  SUPHELI: { bg: '#ffebee', icon: '❓', color: '#c62828', label: 'Şüpheli' },
  YOK: { bg: '#fafafa', icon: '❌', color: colors.textSecondary, label: 'Eşleşme Yok' },
}[tip] || { bg: '#fafafa', icon: '?', color: colors.textSecondary, label: 'Bilinmiyor' });

export default function ExcelSayimImportPage({ embedded = false }) {
  const [asama, setAsama] = useState('yukleme'); // yukleme | onizleme | sonuc
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [lokasyonId, setLokasyonId] = useState('AUTO');
  const [dosya, setDosya] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [satirDegisimleri, setSatirDegisimleri] = useState({}); // {excel_satir: {id, kod, ad, birim}}
  const [atlananlar, setAtlananlar] = useState(new Set());
  const [aliasKaydet, setAliasKaydet] = useState(true);
  const [sonuc, setSonuc] = useState(null);
  const [secimModal, setSecimModal] = useState(null); // {excel_satir, alternatifler}
  const [modalArama, setModalArama] = useState('');
  const [modalMalzemeler, setModalMalzemeler] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => setLokasyonlar(Array.isArray(d) ? d.filter(l => l.aktif !== false) : []))
      .catch(() => setLokasyonlar([]));
  }, []);

  const handleDosyaSec = (file) => {
    if (!file) return;
    const uzanti = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(uzanti)) {
      setMsg({ type: 'error', text: 'Sadece .xlsx, .xls, .csv destekleniyor' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMsg({ type: 'error', text: 'Dosya çok büyük (max 10 MB)' });
      return;
    }
    setDosya(file);
    setMsg(null);
  };

  const handleYukle = async () => {
    if (!dosya) {
      setMsg({ type: 'error', text: 'Dosya seçin' });
      return;
    }
    setLoading(true);
    setMsg(null);
    try {
      const formData = new FormData();
      formData.append('file', dosya);
      // lokasyonId boşsa (="AUTO") backend multi-lokasyon moduna geçer
      const url = lokasyonId && lokasyonId !== 'AUTO'
        ? `${API_URL}/api/stok-sayim/excel-upload?lokasyon_id=${lokasyonId}`
        : `${API_URL}/api/stok-sayim/excel-upload`;
      const res = await fetch(url, { method: 'POST', headers: getAuthHeaders(), body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Yükleme hatası');
      setPreviewData(data);
      setAsama('onizleme');
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleAtla = (excel_satir) => {
    setAtlananlar(prev => new Set(prev).add(excel_satir));
  };

  const handleIptalAtla = (excel_satir) => {
    setAtlananlar(prev => {
      const next = new Set(prev);
      next.delete(excel_satir);
      return next;
    });
  };

  const openSecimModal = (row) => {
    setSecimModal(row);
    setModalArama('');
    // Tüm malzemeleri backend'den çek
    fetch(`${API_URL}/api/malzemeler`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => setModalMalzemeler(Array.isArray(d) ? d : []))
      .catch(() => setModalMalzemeler([]));
  };

  const secMalzeme = (m) => {
    setSatirDegisimleri(prev => ({
      ...prev,
      [secimModal.excel_satir]: { id: m.id, kod: m.kod, ad: m.ad, birim: m.birim },
    }));
    setSecimModal(null);
  };

  const getEffectiveMalzeme = (row) => {
    const degisim = satirDegisimleri[row.excel_satir];
    if (degisim) return degisim;
    return row.onerilen_malzeme;
  };

  const handleKaydet = async () => {
    if (!previewData) return;
    const onaylananKalemler = previewData.rows
      .filter(r => !atlananlar.has(r.excel_satir))
      .map(r => {
        const m = getEffectiveMalzeme(r);
        return m ? {
          excel_adi: r.excel_adi,
          malzeme_id: m.id,
          sayilan_miktar: r.excel_miktar,
          lokasyon_id: r.lokasyon_id || null,  // her satır kendi lokasyonuna yazılır
        } : null;
      })
      .filter(Boolean);

    // Onay bekleyen şüpheli/eşleşmeyen satırlar kontrolü
    const onayBekleyen = previewData.rows.filter(r => {
      if (atlananlar.has(r.excel_satir)) return false;
      if (satirDegisimleri[r.excel_satir]) return false;
      return !r.otomatik_onayli;
    });

    if (onayBekleyen.length > 0) {
      setMsg({
        type: 'error',
        text: `${onayBekleyen.length} satır hâlâ onay bekliyor. Her birine malzeme seçin veya atlayın.`,
      });
      return;
    }

    if (onaylananKalemler.length === 0) {
      setMsg({ type: 'error', text: 'Onaylanacak hiç satır yok' });
      return;
    }

    setLoading(true);
    setMsg(null);
    try {
      const kalemler = onaylananKalemler.map(k => ({
        ...k,
        alias_kaydet: aliasKaydet,
      }));
      const res = await fetch(`${API_URL}/api/stok-sayim/excel-confirm`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lokasyon_id: (lokasyonId && lokasyonId !== 'AUTO') ? parseInt(lokasyonId) : null,
          aciklama: `Excel import - ${dosya?.name || ''}`,
          kalemler,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Kayıt hatası');
      setSonuc(data);
      setAsama('sonuc');
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setAsama('yukleme');
    setDosya(null);
    setPreviewData(null);
    setSatirDegisimleri({});
    setAtlananlar(new Set());
    setSonuc(null);
    setMsg(null);
  };

  // ──────────────────────────────────────────
  // AŞAMA 1: YÜKLEME
  // ──────────────────────────────────────────
  if (asama === 'yukleme') {
    return (
      <div>
        {!embedded && <Header title="Excel Sayım İmport" />}
        <div style={{ padding: 24, maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 22 }}>Excel Sayım İmport</h2>
          <p style={{ color: colors.textSecondary, fontSize: 14, marginBottom: 20 }}>
            Sayım dosyanızı yükleyin. Sistem ürünleri otomatik eşleştirmeye çalışır,
            şüpheli eşleşmeleri bir sonraki adımda manuel onaya sunar.
          </p>

          {msg && (
            <div style={{
              padding: '10px 16px', borderRadius: 8, marginBottom: 16,
              background: msg.type === 'error' ? '#ffebee' : '#e8f5e9',
              color: msg.type === 'error' ? '#c62828' : '#2e7d32',
            }}>{msg.text}</div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Lokasyon</label>
            <select value={lokasyonId} onChange={e => setLokasyonId(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`,
                borderRadius: 6, fontSize: 14, boxSizing: 'border-box',
              }}>
              <option value="AUTO">🔄 Otomatik (dosyadaki Lokasyon kolonundan)</option>
              {lokasyonlar.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
            </select>
            <div style={{ fontSize: 11, color: colors.textTertiary, marginTop: 4 }}>
              Otomatik modda dosyadaki her satır kendi lokasyonuna yazılır. Tek lokasyon seçersen sadece o lokasyona ait satırlar işlenir.
            </div>
          </div>

          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); e.currentTarget.style.background = '#e3f2fd'; }}
            onDragLeave={e => { e.currentTarget.style.background = '#fafafa'; }}
            onDrop={e => {
              e.preventDefault();
              e.currentTarget.style.background = '#fafafa';
              handleDosyaSec(e.dataTransfer.files[0]);
            }}
            style={{
              border: '2px dashed #ccc', borderRadius: 12, padding: 40,
              textAlign: 'center', cursor: 'pointer', background: '#fafafa',
              transition: 'background 0.2s', marginBottom: 20,
            }}
          >
            <div style={{ fontSize: 48, marginBottom: 10 }}>📄</div>
            {dosya ? (
              <div>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>{dosya.name}</div>
                <div style={{ fontSize: 12, color: colors.textSecondary }}>
                  {(dosya.size / 1024).toFixed(1)} KB · Dosyayı değiştirmek için tıklayın
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  Dosyayı buraya sürükle veya tıklayıp seç
                </div>
                <div style={{ fontSize: 12, color: colors.textSecondary }}>
                  Desteklenen: .xlsx, .xls, .csv (max 10 MB)
                </div>
                <div style={{ fontSize: 12, color: colors.textTertiary, marginTop: 4 }}>
                  Format: A sütunu = Ürün Adı, B sütunu = Miktar
                </div>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={e => handleDosyaSec(e.target.files[0])}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button
              onClick={handleYukle}
              disabled={!dosya || loading}
              style={{
                padding: '10px 24px',
                background: (!dosya || loading) ? '#ccc' : styles.primary,
                color: 'white', border: 'none', borderRadius: 8,
                fontSize: 14, fontWeight: 700,
                cursor: (!dosya || loading) ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Yükleniyor...' : 'İleri →'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────
  // AŞAMA 2: ÖNİZLEME + ONAY
  // ──────────────────────────────────────────
  if (asama === 'onizleme' && previewData) {
    const onayBekleyen = previewData.rows.filter(r => {
      if (atlananlar.has(r.excel_satir)) return false;
      if (satirDegisimleri[r.excel_satir]) return false;
      return !r.otomatik_onayli;
    }).length;

    const onaylanacakSayi = previewData.rows.filter(r => {
      if (atlananlar.has(r.excel_satir)) return false;
      return getEffectiveMalzeme(r) != null;
    }).length;

    return (
      <div>
        {!embedded && <Header title="Excel Sayım İmport - Önizleme" />}
        <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 22 }}>Önizleme & Onay</h2>
          <p style={{ color: colors.textSecondary, fontSize: 13, marginBottom: 16 }}>
            {previewData.lokasyon_ad} · {previewData.toplam_satir} satır
          </p>

          {msg && (
            <div style={{
              padding: '10px 16px', borderRadius: 8, marginBottom: 16,
              background: msg.type === 'error' ? '#ffebee' : '#e8f5e9',
              color: msg.type === 'error' ? '#c62828' : '#2e7d32',
            }}>{msg.text}</div>
          )}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <div style={{ padding: '8px 16px', background: '#e8f5e9', borderRadius: 6, fontSize: 13 }}>
              ✅ Tam: <strong>{previewData.ozet.tam_eslesme}</strong>
            </div>
            <div style={{ padding: '8px 16px', background: '#e3f2fd', borderRadius: 6, fontSize: 13 }}>
              ⚡ Alias: <strong>{previewData.ozet.alias_eslesme}</strong>
            </div>
            <div style={{ padding: '8px 16px', background: '#fff3e0', borderRadius: 6, fontSize: 13 }}>
              ⚠️ Yüksek: <strong>{previewData.ozet.yuksek_guven}</strong>
            </div>
            <div style={{ padding: '8px 16px', background: '#ffebee', borderRadius: 6, fontSize: 13 }}>
              ❓ Şüpheli: <strong>{previewData.ozet.supheli}</strong>
            </div>
            <div style={{ padding: '8px 16px', background: '#fafafa', borderRadius: 6, fontSize: 13 }}>
              ❌ Eşleşmeyen: <strong>{previewData.ozet.eslesmeyen}</strong>
            </div>
          </div>

          {previewData.uyarilar && previewData.uyarilar.length > 0 && (
            <div style={{
              padding: 10, background: '#fff3e0', borderRadius: 6,
              fontSize: 12, marginBottom: 16, color: '#e65100',
            }}>
              <strong>⚠️ Parse uyarıları:</strong>
              <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                {previewData.uyarilar.slice(0, 5).map((u, i) => <li key={i}>{u}</li>)}
                {previewData.uyarilar.length > 5 && <li>+{previewData.uyarilar.length - 5} daha...</li>}
              </ul>
            </div>
          )}

          <div style={{ background: colors.surface, borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            <div style={{ overflowX: 'auto', maxHeight: 500 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: colors.backgroundAlt, position: 'sticky', top: 0, zIndex: 1 }}>
                    <th style={{ padding: 10, textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>#</th>
                    <th style={{ padding: 10, textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Lokasyon</th>
                    <th style={{ padding: 10, textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Excel Adı</th>
                    <th style={{ padding: 10, textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Eşleşen Sistem Ürünü</th>
                    <th style={{ padding: 10, textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>Miktar</th>
                    <th style={{ padding: 10, textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.rows.map(r => {
                    const atlandi = atlananlar.has(r.excel_satir);
                    const manuel = satirDegisimleri[r.excel_satir];
                    const effectiveTip = manuel ? 'MANUEL' : r.eslesme_tipi;
                    const stil = manuel
                      ? { bg: '#e3f2fd', icon: '✏️', color: '#1565c0', label: 'Manuel' }
                      : tipRengi(r.eslesme_tipi);
                    const effectiveMalzeme = manuel || r.onerilen_malzeme;

                    return (
                      <tr
                        key={r.excel_satir}
                        style={{
                          borderBottom: `1px solid ${colors.border}`,
                          opacity: atlandi ? 0.4 : 1,
                          background: effectiveTip === 'SUPHELI' || effectiveTip === 'YOK' ? '#fff9f9' : '#fff',
                        }}
                      >
                        <td style={{ padding: 10, color: colors.textTertiary, fontFamily: 'monospace', fontSize: 11 }}>{r.excel_satir}</td>
                        <td style={{ padding: 10, fontSize: 12 }}>
                          <span style={{ padding: '2px 8px', background: '#eef', borderRadius: 4, color: '#1565c0', fontWeight: 600 }}>
                            {r.lokasyon_ad || '-'}
                          </span>
                          {r.excel_lokasyon && r.excel_lokasyon !== r.lokasyon_ad && (
                            <div style={{ fontSize: 10, color: colors.textTertiary, marginTop: 2 }}>Excel: {r.excel_lokasyon}</div>
                          )}
                        </td>
                        <td style={{ padding: 10, fontWeight: 600 }}>{r.excel_adi}</td>
                        <td style={{ padding: 10 }}>
                          {effectiveMalzeme ? (
                            <div>
                              <span style={{
                                padding: '2px 8px', background: stil.bg, color: stil.color,
                                borderRadius: 4, fontSize: 11, marginRight: 6, fontWeight: 600,
                              }}>
                                {stil.icon} {manuel ? 'Manuel' : `%${r.guven_yuzdesi}`}
                              </span>
                              {effectiveMalzeme.ad}
                              <span style={{ color: colors.textTertiary, fontSize: 11, marginLeft: 6 }}>
                                ({effectiveMalzeme.kod})
                              </span>
                            </div>
                          ) : (
                            <span style={{ color: '#c62828', fontWeight: 600 }}>
                              ❌ Eşleşme yok — manuel seçim gerekli
                            </span>
                          )}
                        </td>
                        <td style={{ padding: 10, textAlign: 'right', fontWeight: 700 }}>
                          {r.excel_miktar}
                        </td>
                        <td style={{ padding: 10, textAlign: 'center' }}>
                          {atlandi ? (
                            <button onClick={() => handleIptalAtla(r.excel_satir)}
                              style={{
                                padding: '4px 10px', background: '#fff3e0',
                                border: '1px solid #ffb74d', borderRadius: 4,
                                fontSize: 11, cursor: 'pointer',
                              }}>
                              Geri Al
                            </button>
                          ) : (
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                              <button onClick={() => openSecimModal(r)}
                                style={{
                                  padding: '4px 10px', background: '#e3f2fd',
                                  border: '1px solid #90caf9', borderRadius: 4,
                                  fontSize: 11, cursor: 'pointer', color: '#1565c0',
                                }}>
                                {effectiveMalzeme ? 'Değiştir' : 'Seç'}
                              </button>
                              <button onClick={() => handleAtla(r.excel_satir)}
                                style={{
                                  padding: '4px 10px', background: colors.surface,
                                  border: `1px solid ${colors.border}`, borderRadius: 4,
                                  fontSize: 11, cursor: 'pointer',
                                }}>
                                Atla
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={aliasKaydet} onChange={e => setAliasKaydet(e.target.checked)} />
              <span style={{ fontSize: 13 }}>Onaylanan eşleşmeleri hatırla (sonraki import'larda otomatik)</span>
            </label>
          </div>

          {onayBekleyen > 0 && (
            <div style={{
              marginTop: 12, padding: 10, background: '#fff3e0',
              borderRadius: 6, fontSize: 13, color: '#e65100',
            }}>
              ⚠️ {onayBekleyen} satır hâlâ onay bekliyor (şüpheli veya eşleşmeyen). Her birine malzeme seçin veya atlayın.
            </div>
          )}

          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
            <button onClick={resetAll} style={{
              padding: '10px 20px', background: colors.background,
              border: `1px solid ${colors.border}`, borderRadius: 8,
              cursor: 'pointer', fontSize: 14,
            }}>← İptal</button>
            <button
              onClick={handleKaydet}
              disabled={loading || onayBekleyen > 0 || onaylanacakSayi === 0}
              style={{
                padding: '10px 24px',
                background: (loading || onayBekleyen > 0 || onaylanacakSayi === 0) ? '#ccc' : styles.primary,
                color: 'white', border: 'none', borderRadius: 8,
                fontSize: 14, fontWeight: 700,
                cursor: (loading || onayBekleyen > 0 || onaylanacakSayi === 0) ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Kaydediliyor...' : `Onayla & Kaydet (${onaylanacakSayi}) →`}
            </button>
          </div>
        </div>

        {/* Malzeme Seçim Modal */}
        {secimModal && (
          <div
            onClick={() => setSecimModal(null)}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', zIndex: 9999,
              padding: 20,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: colors.surface, borderRadius: 12, padding: 24,
                width: 600, maxWidth: '95vw', maxHeight: '85vh',
                overflow: 'hidden', display: 'flex', flexDirection: 'column',
              }}
            >
              <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>
                Malzeme Seç — "{secimModal.excel_adi}"
              </h3>

              <input
                type="text"
                placeholder="Malzeme ara..."
                value={modalArama}
                onChange={e => setModalArama(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`,
                  borderRadius: 6, fontSize: 14, boxSizing: 'border-box', marginBottom: 12,
                }}
                autoFocus
              />

              <div style={{ flex: 1, overflow: 'auto', marginBottom: 12 }}>
                {secimModal.alternatif_oneriler && secimModal.alternatif_oneriler.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, color: colors.textSecondary, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase' }}>
                      Önerilen Alternatifler
                    </div>
                    {secimModal.alternatif_oneriler.map(alt => (
                      <div
                        key={`alt-${alt.id}`}
                        onClick={() => secMalzeme(alt)}
                        style={{
                          padding: 10, border: `1px solid ${colors.border}`, borderRadius: 6,
                          marginBottom: 6, cursor: 'pointer', background: colors.backgroundAlt,
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#e3f2fd'}
                        onMouseLeave={e => e.currentTarget.style.background = '#f8f9fa'}
                      >
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{alt.ad}</div>
                        <div style={{ fontSize: 11, color: colors.textSecondary }}>
                          {alt.kod} · {alt.birim} · %{alt.guven_yuzdesi} güven
                        </div>
                      </div>
                    ))}
                    <div style={{ borderTop: `1px solid ${colors.border}`, margin: '12px 0 8px' }} />
                  </>
                )}

                <div style={{ fontSize: 11, color: colors.textSecondary, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase' }}>
                  Tüm Malzemeler
                </div>
                {modalMalzemeler
                  .filter(m => searchMatch(`${m.kod} ${m.ad}`, modalArama))
                  .slice(0, 30)
                  .map(m => (
                    <div
                      key={m.id}
                      onClick={() => secMalzeme(m)}
                      style={{
                        padding: 8, border: `1px solid ${colors.border}`, borderRadius: 4,
                        marginBottom: 4, cursor: 'pointer', fontSize: 12,
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                    >
                      <strong>{m.ad}</strong>
                      <span style={{ color: colors.textSecondary, marginLeft: 6 }}>({m.kod} · {m.birim})</span>
                    </div>
                  ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  onClick={() => setSecimModal(null)}
                  style={{
                    padding: '8px 16px', background: colors.background,
                    border: `1px solid ${colors.border}`, borderRadius: 6, cursor: 'pointer', fontSize: 13,
                  }}
                >Kapat</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ──────────────────────────────────────────
  // AŞAMA 3: SONUÇ
  // ──────────────────────────────────────────
  if (asama === 'sonuc' && sonuc) {
    return (
      <div>
        {!embedded && <Header title="Excel Sayım İmport - Sonuç" />}
        <div style={{ padding: 24, maxWidth: 700, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', padding: 30, background: '#e8f5e9', borderRadius: 12, marginBottom: 20 }}>
            <div style={{ fontSize: 48 }}>✅</div>
            <h2 style={{ margin: '10px 0 8px', color: '#2e7d32' }}>Sayım Başarıyla Kaydedildi</h2>
            <p style={{ color: '#555', fontSize: 14 }}>{previewData?.lokasyon_ad}</p>
          </div>

          <div style={{ background: colors.surface, borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 20 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 15 }}>Özet</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 14 }}>
              <div><strong>Kaydedilen kalem:</strong> {sonuc.kaydedilen_kalem}</div>
              <div><strong>Atlanan:</strong> {sonuc.atlanan}</div>
              <div><strong>Yeni öğrenilen eşleşme:</strong> {sonuc.yeni_alias}</div>
              <div><strong>Toplam stok farkı:</strong> {sonuc.toplam_fark > 0 ? '+' : ''}{sonuc.toplam_fark}</div>
            </div>
            {sonuc.uyarilar && sonuc.uyarilar.length > 0 && (
              <div style={{ marginTop: 12, padding: 10, background: '#fff3e0', borderRadius: 6, fontSize: 12 }}>
                <strong>⚠️ Uyarılar:</strong>
                <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                  {sonuc.uyarilar.map((u, i) => <li key={i}>{u}</li>)}
                </ul>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={resetAll}
              style={{
                padding: '10px 20px', background: styles.primary, color: 'white',
                border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700,
              }}
            >
              Yeni İmport
            </button>
            <button
              onClick={() => window.location.href = '/stok-depo'}
              style={{
                padding: '10px 20px', background: colors.background,
                border: `1px solid ${colors.border}`, borderRadius: 8, cursor: 'pointer',
              }}
            >
              Stok Durumuna Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
